// vim: set ts=2 sts=2 sw=2 et:
//
// Mantiene viva la sesión en vivo del operador mientras haya un Platform Live Profile activo. La pantalla
// de Levantamientos (`/?lifting`) se monta una sola vez acá, fuera de las rutas. En el resto de las
// pantallas queda reducida a la barra de tarima (atleta en curso, veredicto y "Siguiente"), así el
// operador sigue la tanda desde Récords, Pesaje o cualquier otra. En las de display no se muestra.

import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import type { GlobalState } from "../../types/stateTypes";
import { buildLiftingLocationSearch } from "../live/browserPlatformLiveClient";
import { isWrpfChromeFreePath } from "../isWrpfChromeFreePath";
import ClassicLiftingLiveContainer from "./ClassicLiftingLiveContainer";
import type { OperatorPresentation } from "./OperationLayerContainer";

/** `true` cuando el host ya muestra `/?lifting`: RootContainer no debe montar una segunda copia. */
export const OperatorLiveHostContext = React.createContext(false);

const isLiftingLocation = (pathname: string, search: string): boolean =>
  pathname === "/" && new URLSearchParams(search).has("lifting");

const OperatorLiveHost = ({ children }: { children: React.ReactNode }) => {
  const activeProfile = useSelector((state: GlobalState) => state.wrpfLive.activeProfile);
  const location = useLocation();
  // La búsqueda de `/?lifting` define la URL del WebSocket: se conserva la última para que navegar no
  // cambie la prop y no reconecte.
  const lastLiftingSearch = React.useRef<string | null>(null);

  const onLifting = isLiftingLocation(location.pathname, location.search);
  const presentation: OperatorPresentation = onLifting
    ? "full"
    : isWrpfChromeFreePath(location.pathname)
      ? "hidden"
      : "dock";
  if (activeProfile === null) {
    lastLiftingSearch.current = null;
  } else if (onLifting) {
    lastLiftingSearch.current = location.search;
  }
  const liftingSearch =
    activeProfile === null
      ? null
      : (lastLiftingSearch.current ?? buildLiftingLocationSearch(activeProfile, window.location.origin));

  // Misma estructura con y sin perfil, para que activarlo no remonte la pantalla actual.
  return (
    <OperatorLiveHostContext.Provider value={liftingSearch !== null}>
      {children}
      {liftingSearch === null ? null : (
        <ClassicLiftingLiveContainer locationSearch={liftingSearch} presentation={presentation} />
      )}
    </OperatorLiveHostContext.Provider>
  );
};

export default OperatorLiveHost;
